/*
 * Content shared between trees, and the rules every tree follows.
 *
 *
 * EVERY OPTION IS A STATEMENT THE CUSTOMER MAKES ABOUT THEMSELVES.
 *
 * First person, present tense, declarative: "My sensor is chirping", "I need to
 * clean my sensor", "I am moving to a new home". Never a question, never a bare
 * topic, and never a mix of the three down one list.
 *
 * The mix is what makes a menu hard to read. Scanning a list where one row is a
 * symptom, the next is a topic and the third is a question means re-orienting
 * at every line, and the reader ends up matching keywords instead of finding
 * themselves in a sentence.
 *
 * Questions are the easiest one to slip back into, because support content
 * tends to be written as an FAQ. "How do I clean a sensor?" is a heading from a
 * manual, not something a person says when asked how we can help. It becomes
 * "I need to clean my sensor".
 *
 * The one place the shape shifts is a node asking the customer to REPORT
 * something they can see, where "It flashes yellow, once a minute" is what they
 * would actually say and forcing it into "I see it flash yellow once a minute"
 * only adds words. Those nodes still hold one shape across all their options,
 * with the last row falling back to first person for the customer who cannot
 * make the observation at all.
 *
 * Buttons are not options. "Arrange a replacement" on a document is an action
 * the customer takes, so it stays imperative.
 *
 *
 * OPTION HELP TEXT DOES NOT GIVE THE ANSWER.
 *
 * `help` exists to make an option easier to CHOOSE, never to say what choosing
 * it will produce. "Powder, crust, discoloration" helps somebody decide
 * whether what they are looking at counts as corrosion. "The smoke chamber
 * needs cleaning" hands them the verdict before they have answered, which both
 * spoils the step that was going to explain it properly and invites them to
 * pick the outcome they want rather than the one they see.
 *
 * If an option cannot be made easier to choose, it gets no help text.
 *
 *
 * NO EM DASHES, ANYWHERE.
 *
 * Not in copy, not in comments. Real people write in commas, semicolons and
 * full stops, and a page full of em dashes reads as machine written whatever
 * else is true about it.
 */

// Marks copy that has not been through review yet. See engine/validate.js.
// draftsIn() lists these so a tree cannot quietly ship unapproved safety copy.
export const DRAFT = 'Interim wording. Replace before this brand goes live.';

/*
 * Two brands give the same advice about moving house, and it is advice with
 * safety consequences. "Do not deactivate on your way out the door" is the
 * whole point of it, so it is held once rather than copied, and a correction to
 * it cannot land on one brand and miss the other.
 *
 * A FACTORY rather than a constant, because node ids and the destination of
 * "something else is wrong" differ per tree, and a shared object that two trees
 * mutate into shape would be a bug waiting to happen.
 */
export const movingDocument = ({
  menu,
  reason = 'moving_service'
}) => ({
  body: [{
    text: 'Do not deactivate a sensor. Deactivation is permanent and is only for disposal. A deactivated sensor cannot be turned back on, and it cannot protect anybody in your new home.',
    type: 'warning'
  }, 'Your sensors are yours and they travel with you. A little preparation before moving day saves a service call at the other end.', {
    items: ['Number and label each sensor as you take it down, so you know which room it came from.', 'Keep them together with your personal items rather than in the moving truck. They are small enough to be lost and important enough not to be.', 'They stay on the same network. Nothing needs to be paired again as long as they move together.', 'If possible, have your dealership take them down and put them back up. Placement is important, and the recommended placement is different in every home.'],
    type: 'steps'
  }, 'If you are moving to a different state, we will link you with a dealership who covers your new address.'],
  continue: {
    label: 'I have a different problem',
    next: menu
  },
  escalate: {
    help: 'Taking sensors down and putting them back up is a service visit, not a job for moving day. Tell us when you are going and we will get it booked.',
    label: 'Set up service for my move',
    reason: reason
  },
  resolution: 'escalate',
  title: 'Moving to a new home',
  type: 'document'
});

/*
 * The escape hatch every tree carries. "Something else" does not collect a
 * message here. It enters the escalation sub-flow, which asks for one at the
 * end alongside the contact details. Asking for the message twice, once as a
 * tree node and once as an escalation field, is how you end up with two half
 * written descriptions of the same problem.
 */
export const otherEscalation = summary => ({
  reason: 'other',
  summary: summary || 'Tell us what is going on in your own words and we will get it to the right person, along with everything you have already told us.',
  title: 'Tell us what is happening',
  type: 'escalate'
});